import { useEffect, useState, useCallback } from 'react';

/**
 * PWA install prompt + standalone-mode detection.
 *
 * Listens for the browser's `beforeinstallprompt` event (Chromium-based
 * browsers + modern Edge) and exposes an imperative `promptInstall()`
 * function the UI can call to show the native install dialog. iOS Safari
 * does not fire this event — the consumer should fall back to the
 * "tap Share → Add to Home Screen" instructions when `canPrompt` is
 * `false` AND `isIOS` is `true`.
 *
 * `isStandalone` returns `true` whenever the page is launched from a
 * Home Screen icon (iOS) or in installed PWA mode (Chromium).
 */
export default function usePwaInstall() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return window.localStorage.getItem('lumera.pwa.installPromptDismissedAt'); }
    catch (_) { return null; }
  });

  const isIOS = typeof navigator !== 'undefined'
    && /iphone|ipad|ipod/i.test(navigator.userAgent || '');

  const isStandalone = typeof window !== 'undefined'
    && (window.matchMedia?.('(display-mode: standalone)').matches
        || window.navigator.standalone === true
        || document.referrer?.startsWith('android-app://'));

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return null;
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice?.outcome || null;   // 'accepted' | 'dismissed'
  }, [deferred]);

  const dismiss = useCallback(() => {
    const ts = String(Date.now());
    try { window.localStorage.setItem('lumera.pwa.installPromptDismissedAt', ts); }
    catch (_) { /* ignore */ }
    setDismissed(ts);
  }, []);

  // Re-show install hint after 7 days of dismissal.
  const showHint = (() => {
    if (installed || isStandalone) return false;
    if (!dismissed) return true;
    const days = (Date.now() - Number(dismissed)) / 86_400_000;
    return days >= 7;
  })();

  return {
    canPrompt: !!deferred,    // native dialog available
    isIOS,
    isStandalone,
    installed,
    showHint,
    promptInstall,
    dismiss,
  };
}
